import React, { useState, useEffect } from 'react';
import { Form, InputGroup, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

/**
 * Advanced inline filter component
 * Supports: text, date, numeric ranges, and select filters
 */
const AdvancedFilter = ({ column, dateColumns = [], numericColumns = [] }) => {
  const columnFilterValue = column.getFilterValue();
  const { filterVariant, filterOptions } = column.columnDef.meta || {};
  
  // Date columns
  const isDateColumn = dateColumns.includes(column.id);
  
  // Numeric columns
  const isNumericColumn = numericColumns.includes(column.id);
  
  // Select columns
  const isSelectColumn = filterVariant === 'select';

  // Text filter state — tutti gli hook DEVONO stare qui in cima, prima di qualsiasi return condizionale
  const [localValue, setLocalValue] = useState(columnFilterValue ?? '');

  // Sincronizza il valore locale se il filtro esterno viene resettato
  useEffect(() => {
    setLocalValue(columnFilterValue ?? '');
  }, [columnFilterValue]);

  // Debounce: aggiorna il filtro della colonna 400ms dopo che l'utente smette di digitare
  // Attivo solo per il filtro testo (isDateColumn/isNumericColumn/isSelectColumn gestiscono onChange direttamente)
  useEffect(() => {
    if (isDateColumn || isNumericColumn || isSelectColumn) return;
    const timer = setTimeout(() => {
      const newValue = localValue || undefined;
      // Guard: non chiamare setFilterValue se il valore non è cambiato (evita loop di fetch)
      if (column.getFilterValue() === newValue) return;
      column.setFilterValue(newValue);
    }, 400);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localValue, isDateColumn, isNumericColumn, isSelectColumn]);
  
  const handleClear = () => {
    column.setFilterValue(undefined);
  };

  const handleTextClear = () => {
    setLocalValue('');
    column.setFilterValue(undefined);
  };
  
  // Date filter with presets
  if (isDateColumn) {
    return (
      <InputGroup size="sm">
        <Form.Control
          type="date"
          value={columnFilterValue || ''}
          onChange={(e) => column.setFilterValue(e.target.value)}
          style={{ fontSize: '0.75rem' }}
        />
        {columnFilterValue && (
          <Button variant="outline-secondary" size="sm" onClick={handleClear} title="Rimuovi filtro">
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        )}
      </InputGroup>
    );
  }
  
  // Numeric range filter
  if (isNumericColumn) {
    const [min, max] = Array.isArray(columnFilterValue) ? columnFilterValue : [columnFilterValue, undefined];
    return (
      <div className="d-flex gap-1">
        <Form.Control
          type="number"
          placeholder="Min"
          value={min ?? ''}
          onChange={(e) => {
            const val = e.target.value;
            column.setFilterValue((old) => [val, old?.[1]]);
          }}
          size="sm"
          style={{ fontSize: '0.75rem', width: '50%' }}
        />
        <Form.Control
          type="number"
          placeholder="Max"
          value={max ?? ''}
          onChange={(e) => {
            const val = e.target.value;
            column.setFilterValue((old) => [old?.[0], val]);
          }}
          size="sm"
          style={{ fontSize: '0.75rem', width: '50%' }}
        />
        {(min || max) && (
          <Button variant="outline-secondary" size="sm" onClick={handleClear} title="Rimuovi filtro" style={{ padding: '0.25rem 0.4rem' }}>
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        )}
      </div>
    );
  }
  
  // Select filter
  if (isSelectColumn && filterOptions) {
    return (
      <InputGroup size="sm">
        <Form.Select
          value={columnFilterValue ?? ''}
          onChange={(e) => {
            const value = e.target.value;
            if (value === '') {
              column.setFilterValue(undefined);
            } else if (value === 'true') {
              column.setFilterValue(true);
            } else if (value === 'false') {
              column.setFilterValue(false);
            } else {
              column.setFilterValue(value);
            }
          }}
          size="sm"
          style={{ fontSize: '0.75rem' }}
        >
          <option value="">Tutti</option>
          {filterOptions.map((option) => {
            // Handle both simple values and {value, label} objects
            const optionValue = typeof option === 'object' && option !== null ? option.value : option;
            const optionLabel = typeof option === 'object' && option !== null ? option.label : option;
            return (
              <option key={optionValue} value={optionValue}>
                {optionLabel}
              </option>
            );
          })}
        </Form.Select>
        {columnFilterValue !== undefined && columnFilterValue !== '' && (
          <Button variant="outline-secondary" size="sm" onClick={handleClear} title="Rimuovi filtro">
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        )}
      </InputGroup>
    );
  }
  
  // Text filter (default) — usa stato locale + debounce per evitare perdita focus ad ogni tasto
  return (
    <InputGroup size="sm">
      <Form.Control
        type="text"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        placeholder="Cerca..."
        size="sm"
        style={{ fontSize: '0.75rem' }}
      />
      {localValue && (
        <Button variant="outline-secondary" size="sm" onClick={handleTextClear} title="Rimuovi filtro">
          <FontAwesomeIcon icon={faTimes} />
        </Button>
      )}
    </InputGroup>
  );
};

export default AdvancedFilter;

